import { useMutation, useQuery } from '@tanstack/react-query';

import thematicMeetingService from './thematicMeetingService';

import type { PaginatedResponse } from '@/types/api';
import type {
    CreateThematicMeetingRequest,
    EditableMeetingTemplate,
    MeetingTemplate,
    MeetingTemplateCreateRequest,
    MeetingTemplateUpdateRequest,
    OverdueEmployee,
    ThematicMeeting,
    ThematicMeetingAgenda,
    ThematicMeetingAgendaUpdateRequest,
    ThematicMeetingCalendarLinks,
    ThematicMeetingCalendarLinksParams,
    ThematicMeetingListItem,
    ThematicMeetingListParams,
    ThematicMeetingStats,
    ThematicMeetingTranscript,
    UpdateThematicMeetingScheduleRequest,
} from '@/types/thematic-meetings';

import { queryClient } from '@/AppWithProviders';
import {
    ThematicMeetingMutationKey,
    ThematicMeetingQueryKey,
    thematicMeetingKey,
    thematicMeetingPlanKey,
    thematicMeetingTranscriptKey,
} from '@/constants/QueryKeys';
import { useAmelioInfiniteQuery } from '@/hooks/api/useAmelioInfiniteQuery';

const invalidateThematicMeetingLists = () => {
    queryClient.invalidateQueries({ queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETINGS] });
    queryClient.invalidateQueries({ queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETING_STATS] });
    queryClient.invalidateQueries({ queryKey: [ThematicMeetingQueryKey.OVERDUE_EMPLOYEES] });
};

/**
 * Writes the saved meeting straight into the detail cache rather than refetching it: a refetch still in
 * flight could land after a later save and put back what that save replaced.
 */
const setThematicMeetingCache = (id: string, meeting: ThematicMeeting) => {
    queryClient.setQueryData(thematicMeetingKey(id), meeting);
};

// The links encode the title, the date and the duration: editing any of them makes them stale.
const invalidateCalendarLinks = (id: string) => {
    queryClient.invalidateQueries({
        queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETING_CALENDAR_LINKS, id],
    });
};

export const useGetThematicMeetings = (params: ThematicMeetingListParams) => {
    return useQuery<PaginatedResponse<ThematicMeetingListItem>>({
        queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETINGS, params],
        queryFn: () => thematicMeetingService.getThematicMeetings(params),
    });
};

export const useGetThematicMeeting = (id: string) => {
    return useQuery<ThematicMeeting>({
        queryKey: thematicMeetingKey(id),
        queryFn: () => thematicMeetingService.getThematicMeeting(id),
        enabled: !!id,
    });
};

/**
 * The plan a meeting follows, whether the company still offers it, put it back to draft or deleted it
 * since: `useGetMeetingTemplates` lists only the plans offered for new meetings.
 */
export const useGetThematicMeetingPlan = (meetingId: string) => {
    return useQuery<MeetingTemplate>({
        queryKey: thematicMeetingPlanKey(meetingId),
        queryFn: () => thematicMeetingService.getThematicMeetingPlan(meetingId),
        enabled: !!meetingId,
        staleTime: Infinity,
    });
};

export const useGetThematicMeetingStats = () => {
    return useQuery<ThematicMeetingStats>({
        queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETING_STATS],
        queryFn: () => thematicMeetingService.getStats(),
    });
};

const OVERDUE_EMPLOYEES_PAGE_SIZE = 20;

export const useGetOverdueEmployees = (searchQuery: string) => {
    return useAmelioInfiniteQuery<OverdueEmployee>({
        queryKey: [ThematicMeetingQueryKey.OVERDUE_EMPLOYEES, searchQuery],
        queryFn: ({ pageNumber, pageSize }) =>
            thematicMeetingService.getOverdueEmployees({
                pageNumber,
                pageSize,
                searchQuery: searchQuery || undefined,
            }),
        pageSize: OVERDUE_EMPLOYEES_PAGE_SIZE,
    });
};

/**
 * The company's plans as its editors manage them, drafts included: what the plan list and the editor
 * read. Managers creating a meeting read `useGetMeetingTemplates`, which offers the active ones only.
 */
export const useGetEditableMeetingTemplates = (options: { enabled?: boolean } = {}) => {
    return useQuery<EditableMeetingTemplate[]>({
        queryKey: [ThematicMeetingQueryKey.EDITABLE_MEETING_TEMPLATES],
        queryFn: () => thematicMeetingService.getEditableTemplates(),
        staleTime: Infinity,
        enabled: options.enabled ?? true,
    });
};

export const useGetMeetingTemplates = () => {
    return useQuery<MeetingTemplate[]>({
        queryKey: [ThematicMeetingQueryKey.MEETING_TEMPLATES],
        queryFn: () => thematicMeetingService.getTemplates(),
        staleTime: Infinity,
    });
};

/** The Amelio catalogue of plans, the ones a company may import a copy of. */
export const useGetMeetingTemplateCatalog = (options: { enabled: boolean }) => {
    return useQuery<MeetingTemplate[]>({
        queryKey: [ThematicMeetingQueryKey.MEETING_TEMPLATE_CATALOG],
        queryFn: () => thematicMeetingService.getTemplateCatalog(),
        staleTime: Infinity,
        enabled: options.enabled,
    });
};

/**
 * A plan the company gains — written or imported — goes straight to the end of the editors' cached
 * list, where the server put it: the editor a creation opens looks the plan up in that list, and would
 * otherwise report it missing until the refetch lands. Neither list goes stale on its own, so both are
 * invalidated; the managers' list is only refetched, since whether it offers the plan is the server's
 * call.
 */
const addToEditableMeetingTemplates = (added: EditableMeetingTemplate) => {
    queryClient.setQueryData<EditableMeetingTemplate[]>(
        [ThematicMeetingQueryKey.EDITABLE_MEETING_TEMPLATES],
        (previous) => previous && [...previous, added],
    );
    queryClient.invalidateQueries({
        queryKey: [ThematicMeetingQueryKey.EDITABLE_MEETING_TEMPLATES],
    });
    queryClient.invalidateQueries({ queryKey: [ThematicMeetingQueryKey.MEETING_TEMPLATES] });
};

export const useCreateMeetingTemplate = () => {
    return useMutation<EditableMeetingTemplate, Error, MeetingTemplateCreateRequest>({
        mutationKey: [ThematicMeetingMutationKey.CREATE_MEETING_TEMPLATE],
        mutationFn: (request) => thematicMeetingService.createTemplate(request),
        onSuccess: addToEditableMeetingTemplates,
    });
};

/** Copies a plan of the Amelio catalogue into the company, named by the catalogue plan's id. */
export const useImportMeetingTemplate = () => {
    return useMutation<EditableMeetingTemplate, Error, string>({
        mutationKey: [ThematicMeetingMutationKey.IMPORT_MEETING_TEMPLATE],
        mutationFn: (catalogTemplateId) => thematicMeetingService.importTemplate(catalogTemplateId),
        onSuccess: addToEditableMeetingTemplates,
    });
};

/**
 * Rewrites one of the company's plans, named by its id.
 *
 * No plan query goes stale on its own. The saved plan is written straight into the editors' list;
 * the other plan queries are invalidated, since a plan turned into a draft or back changes what
 * managers are offered, and its wording shows in the meetings built on it.
 */
export const useUpdateMeetingTemplate = () => {
    return useMutation<
        EditableMeetingTemplate,
        Error,
        { id: string; request: MeetingTemplateUpdateRequest }
    >({
        mutationKey: [ThematicMeetingMutationKey.UPDATE_MEETING_TEMPLATE],
        mutationFn: ({ id, request }) => thematicMeetingService.updateTemplate(id, request),
        onSuccess: (saved) => {
            queryClient.setQueryData<EditableMeetingTemplate[]>(
                [ThematicMeetingQueryKey.EDITABLE_MEETING_TEMPLATES],
                (previous) =>
                    previous?.map((template) => (template.id === saved.id ? saved : template)),
            );
            queryClient.invalidateQueries({
                queryKey: [ThematicMeetingQueryKey.EDITABLE_MEETING_TEMPLATES],
            });
            queryClient.invalidateQueries({
                queryKey: [ThematicMeetingQueryKey.MEETING_TEMPLATES],
            });
            queryClient.invalidateQueries({
                queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETING_PLAN],
            });
        },
    });
};

export const useCreateThematicMeeting = () => {
    return useMutation<ThematicMeeting, Error, CreateThematicMeetingRequest>({
        mutationKey: [ThematicMeetingMutationKey.CREATE_THEMATIC_MEETING],
        mutationFn: (request) => thematicMeetingService.create(request),
        onSuccess: invalidateThematicMeetingLists,
    });
};

/**
 * The plan page's three editors, one mutation each, so that a save never carries back a field that page
 * read before another save replaced it. The lists are refreshed too: their cards show the date and the
 * context.
 */
export const useUpdateThematicMeetingSchedule = () => {
    return useMutation<
        ThematicMeeting,
        Error,
        { id: string; request: UpdateThematicMeetingScheduleRequest }
    >({
        mutationKey: [ThematicMeetingMutationKey.UPDATE_THEMATIC_MEETING_SCHEDULE],
        mutationFn: ({ id, request }) => thematicMeetingService.updateSchedule(id, request),
        onSuccess: (meeting, { id }) => {
            invalidateThematicMeetingLists();
            setThematicMeetingCache(id, meeting);
            invalidateCalendarLinks(id);
        },
    });
};

export const useUpdateThematicMeetingContext = () => {
    return useMutation<ThematicMeeting, Error, { id: string; context: string | null }>({
        mutationKey: [ThematicMeetingMutationKey.UPDATE_THEMATIC_MEETING_CONTEXT],
        mutationFn: ({ id, context }) => thematicMeetingService.updateContext(id, context),
        onSuccess: (meeting, { id }) => {
            invalidateThematicMeetingLists();
            setThematicMeetingCache(id, meeting);
        },
    });
};

export const useUpdateThematicMeetingPreparationNotes = () => {
    return useMutation<ThematicMeeting, Error, { id: string; preparationNotes: string | null }>({
        mutationKey: [ThematicMeetingMutationKey.UPDATE_THEMATIC_MEETING_PREPARATION_NOTES],
        mutationFn: ({ id, preparationNotes }) =>
            thematicMeetingService.updatePreparationNotes(id, preparationNotes),
        onSuccess: (meeting, { id }) => {
            invalidateThematicMeetingLists();
            setThematicMeetingCache(id, meeting);
        },
    });
};

export const useDeleteThematicMeeting = () => {
    return useMutation<void, Error, string>({
        mutationKey: [ThematicMeetingMutationKey.DELETE_THEMATIC_MEETING],
        mutationFn: (id: string) => thematicMeetingService.remove(id),
        onSuccess: invalidateThematicMeetingLists,
    });
};

export const useCompleteThematicMeeting = () => {
    return useMutation<ThematicMeeting, Error, string>({
        mutationKey: [ThematicMeetingMutationKey.COMPLETE_THEMATIC_MEETING],
        mutationFn: (id: string) => thematicMeetingService.complete(id),
        onSuccess: (meeting, id) => {
            invalidateThematicMeetingLists();
            setThematicMeetingCache(id, meeting);
        },
    });
};

export const useReopenThematicMeeting = () => {
    return useMutation<ThematicMeeting, Error, string>({
        mutationKey: [ThematicMeetingMutationKey.REOPEN_THEMATIC_MEETING],
        mutationFn: (id: string) => thematicMeetingService.reopen(id),
        onSuccess: (meeting, id) => {
            invalidateThematicMeetingLists();
            setThematicMeetingCache(id, meeting);
        },
    });
};

// A GET under the hood, but modeled as a mutation: it only ever runs on demand from a click (never
// on mount, never cached/refetched), and the button needs the per-call `isPending`/error state a
// query's shared cache entry doesn't give an on-demand download.
export const useDownloadThematicMeetingCalendar = () => {
    return useMutation<Blob, Error, { id: string; startAt?: string }>({
        mutationKey: [ThematicMeetingMutationKey.DOWNLOAD_THEMATIC_MEETING_CALENDAR],
        mutationFn: ({ id, startAt }) => thematicMeetingService.getCalendar(id, startAt),
    });
};

// Fetched with the page, so the links are ready before any click: a tab opened after an awaited
// request is what pop-up blockers stop. The addresses only change when the meeting is edited, which
// invalidates them, hence no refetch when the manager comes back from the calendar's tab.
export const useGetThematicMeetingCalendarLinks = ({ id }: ThematicMeetingCalendarLinksParams) => {
    return useQuery<ThematicMeetingCalendarLinks>({
        queryKey: [ThematicMeetingQueryKey.THEMATIC_MEETING_CALENDAR_LINKS, id],
        queryFn: () => thematicMeetingService.getCalendarLinks(id),
        enabled: !!id,
        staleTime: Infinity,
    });
};

export const useUpdateThematicMeetingAgenda = () => {
    return useMutation<
        ThematicMeetingAgenda,
        Error,
        { id: string; request: ThematicMeetingAgendaUpdateRequest }
    >({
        mutationKey: [ThematicMeetingMutationKey.UPDATE_THEMATIC_MEETING_AGENDA],
        mutationFn: ({ id, request }) => thematicMeetingService.updateAgenda(id, request),
        onSuccess: (agenda, { id }) => {
            queryClient.setQueryData<ThematicMeeting>(thematicMeetingKey(id), (previous) =>
                previous ? { ...previous, agenda } : previous,
            );
            invalidateCalendarLinks(id);
        },
    });
};

/**
 * Renames the meeting. The response is written straight into the detail cache, like every other
 * edit of the meeting, and the lists are refreshed because their cards show the title too.
 */
export const useUpdateThematicMeetingTitle = () => {
    return useMutation<ThematicMeeting, Error, { id: string; title: string }>({
        mutationKey: [ThematicMeetingMutationKey.UPDATE_THEMATIC_MEETING_TITLE],
        mutationFn: ({ id, title }) => thematicMeetingService.updateTitle(id, title),
        onSuccess: (meeting, { id }) => {
            invalidateThematicMeetingLists();
            setThematicMeetingCache(id, meeting);
            invalidateCalendarLinks(id);
        },
    });
};

/**
 * The meeting's transcript and the recap written from it. Its file name is null while nothing has been
 * uploaded — absence is a normal state the server answers with a payload, never with an error.
 */
export const useGetThematicMeetingTranscript = (meetingId?: string) => {
    return useQuery<ThematicMeetingTranscript, Error>({
        queryKey: thematicMeetingTranscriptKey(meetingId),
        queryFn: () => thematicMeetingService.getTranscript(meetingId!),
        enabled: !!meetingId,
        refetchOnWindowFocus: false,
    });
};

/**
 * Stores (or replaces) the transcript file.
 *
 * The response is written straight into the cache rather than invalidated: a replacement clears the
 * previous recap server-side, and refetching would leave the panel showing the old recap beside the
 * new file name for as long as the round trip lasts.
 */
export const useUploadThematicMeetingTranscript = (meetingId: string) => {
    return useMutation<ThematicMeetingTranscript, Error, File>({
        mutationKey: [ThematicMeetingMutationKey.UPLOAD_THEMATIC_MEETING_TRANSCRIPT],
        mutationFn: (file) => thematicMeetingService.uploadTranscript(meetingId, file),
        onSuccess: (data) => {
            queryClient.setQueryData(thematicMeetingTranscriptKey(meetingId), data);
        },
    });
};
